import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { CasQuarantineSourceRow } from '../services/api/domains/products/types';

/**
 * Shared source-detail card (issue #662). Originally CasQuarantineReviewPage's
 * "every source with data on this product" comparison card; extracted so
 * CasDecisionPanel's pick-one options can render the same detail view
 * instead of a single line of label text.
 *
 * Do not nest this component inside a clickable label/radio (e.g. MUI's
 * FormControlLabel `label` prop) -- its own IconButton becomes a second,
 * ambiguous click target inside the outer control. CasDecisionPanel renders
 * the radio's FormControlLabel and this card as siblings for exactly this
 * reason; render it the same way in any new caller.
 */

export function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

/**
 * Whether the CAS number / InChIKey shown on `source` disagrees with at
 * least one other card in `allSources` -- computed once per render over
 * every card in the set, not pairwise per card, so a 3-way disagreement
 * highlights all three rather than only the two that happen to be compared
 * first.
 */
export function computeFieldConflicts(
  source: CasQuarantineSourceRow,
  allSources: CasQuarantineSourceRow[]
): { casConflict: boolean; inchiKeyConflict: boolean } {
  const distinctCas = new Set(
    allSources.map(s => s.cas_number).filter((v): v is string => !!v)
  );
  const distinctInchiKey = new Set(
    allSources.map(s => s.inchi_key).filter((v): v is string => !!v)
  );
  return {
    casConflict: !!source.cas_number && distinctCas.size > 1,
    inchiKeyConflict: !!source.inchi_key && distinctInchiKey.size > 1,
  };
}

export const CasSourceCard: React.FC<{
  source: CasQuarantineSourceRow;
  casConflict: boolean;
  inchiKeyConflict: boolean;
}> = ({ source, casConflict, inchiKeyConflict }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Paper variant="outlined" style={styles.sourceCard}>
      <div style={styles.sourceCardHeader}>
        <Typography style={styles.sourceCardTitle}>
          {source.source} ({source.match_basis})
        </Typography>
        <IconButton
          size="small"
          aria-label={
            expanded
              ? `Collapse ${source.source} detail`
              : `Expand ${source.source} detail`
          }
          onClick={() => setExpanded(prev => !prev)}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>
      </div>
      <Typography variant="body2">
        Name: {source.matched_name ?? '—'}
      </Typography>
      <Typography
        variant="body2"
        style={casConflict ? styles.conflictField : undefined}
        data-testid={casConflict ? `cas-conflict-${source.source}` : undefined}>
        CAS: {source.cas_number ?? '—'}
        {casConflict ? ' (conflict)' : ''}
      </Typography>
      <Typography
        variant="body2"
        style={inchiKeyConflict ? styles.conflictField : undefined}
        data-testid={
          inchiKeyConflict ? `inchikey-conflict-${source.source}` : undefined
        }>
        InChIKey: {source.inchi_key ?? '—'}
        {inchiKeyConflict ? ' (conflict)' : ''}
      </Typography>
      <Collapse in={expanded} unmountOnExit>
        <div style={styles.sourceCardDetail}>
          <Typography variant="body2">
            Molecular formula: {source.molecular_formula ?? '—'}
          </Typography>
          <Typography variant="body2">
            Molecular weight: {source.molecular_weight ?? '—'}
          </Typography>
          <Typography variant="body2">
            SMILES: {source.smiles ?? '—'}
          </Typography>
          <Typography variant="body2">InChI: {source.inchi ?? '—'}</Typography>
          <Typography variant="body2">
            Native ID: {source.source_native_id ?? '—'}
          </Typography>
          <Typography variant="body2">
            Queried: {formatTimestamp(source.queried_at)}
          </Typography>
          {source.raw_response !== null && (
            <pre
              style={styles.rawJson}
              data-testid={`raw-response-${source.source}`}>
              {JSON.stringify(source.raw_response, null, 2)}
            </pre>
          )}
        </div>
      </Collapse>
    </Paper>
  );
};

const styles: Record<string, React.CSSProperties> = {
  sourceCard: { padding: '0.75rem' },
  sourceCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sourceCardTitle: { fontWeight: 600 },
  conflictField: {
    fontWeight: 700,
    color: '#92400e',
    backgroundColor: '#fef3c7',
    borderRadius: '0.25rem',
    padding: '0 0.25rem',
    display: 'inline-block',
  },
  sourceCardDetail: {
    marginTop: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  rawJson: {
    maxHeight: '16rem',
    overflow: 'auto',
    fontSize: '0.75rem',
    backgroundColor: '#f8fafc',
    padding: '0.5rem',
    borderRadius: '0.25rem',
  },
};

export default CasSourceCard;
